'use client';

/**
 * FlowingMenu Component
 * =====================
 *
 * A stylish flowing menu with hover animations that reveal
 * a marquee effect with images. Based on React Bits implementation.
 *
 * HOW IT WORKS:
 * 1. Each menu item has a hidden marquee layer
 * 2. On hover, the marquee slides in from the nearest edge (top/bottom)
 * 3. The marquee contains repeated text and images that scroll horizontally
 * 4. GSAP handles the smooth entrance/exit animations
 * 5. Optional onClick handler for custom actions (e.g., opening modals)
 */

import React, { useRef } from 'react';
import { getImageProps } from 'next/image';
import gsap from 'gsap';

export interface MenuItemData {
  link: string;
  text: string;
  image: string;
}

interface FlowingMenuProps {
  items: MenuItemData[];
  onItemClick?: (item: MenuItemData, index: number) => void;
}

/**
 * Calculate distance between two points
 */
const distMetric = (x: number, y: number, x2: number, y2: number): number => {
  const xDiff = x - x2;
  const yDiff = y - y2;
  return xDiff * xDiff + yDiff * yDiff;
};

/**
 * Find the closest edge (top or bottom) to the mouse cursor
 */
const findClosestEdge = (
  mouseX: number,
  mouseY: number,
  width: number,
  height: number,
  left: number,
  top: number
): 'top' | 'bottom' => {
  const topEdgeDist = distMetric(mouseX, mouseY, left + width / 2, top);
  const bottomEdgeDist = distMetric(mouseX, mouseY, left + width / 2, top + height);
  return topEdgeDist < bottomEdgeDist ? 'top' : 'bottom';
};

/**
 * Individual menu item with hover animation
 */
const MenuItem: React.FC<{
  item: MenuItemData;
  index: number;
  onClick?: (item: MenuItemData, index: number) => void;
}> = ({ item, index, onClick }) => {
  const itemRef = useRef<HTMLDivElement>(null);
  const marqueeRef = useRef<HTMLDivElement>(null);
  const marqueeInnerRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!itemRef.current || !marqueeRef.current || !marqueeInnerRef.current) return;

    const rect = itemRef.current.getBoundingClientRect();
    const edge = findClosestEdge(
      e.clientX,
      e.clientY,
      rect.width,
      rect.height,
      rect.left,
      rect.top
    );

    const startY = edge === 'top' ? '-101%' : '101%';

    gsap.timeline({ defaults: { duration: 0.6, ease: 'expo' } })
      .set(marqueeRef.current, { y: startY })
      .set(marqueeInnerRef.current, { y: -startY })
      .to([marqueeRef.current, marqueeInnerRef.current], { y: '0%' });
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!itemRef.current || !marqueeRef.current || !marqueeInnerRef.current) return;

    const rect = itemRef.current.getBoundingClientRect();
    const edge = findClosestEdge(
      e.clientX,
      e.clientY,
      rect.width,
      rect.height,
      rect.left,
      rect.top
    );

    const endY = edge === 'top' ? '-101%' : '101%';

    gsap.timeline({ defaults: { duration: 0.6, ease: 'expo' } })
      .to(marqueeRef.current, { y: endY })
      .to(marqueeInnerRef.current, { y: -endY }, 0);
  };

  // CSS background-images bypass next/image, so the raw 1-4 MB renders were
  // downloaded on page load. getImageProps() gives us the optimised
  // /_next/image URL (resized + WebP/AVIF) for a thumbnail-sized image instead.
  const { props: { src: thumbSrc } } = getImageProps({
    src: item.image,
    alt: '',
    width: 256,
    height: 256,
  });

  // Create 4 repeated instances for continuous marquee effect
  const marqueeContent = Array(4).fill(null).map((_, i) => (
    <React.Fragment key={i}>
      <span className="flowing-menu__marquee-text">{item.text}</span>
      <div
        className="flowing-menu__marquee-img"
        style={{ backgroundImage: `url("${thumbSrc}")` }}
      />
    </React.Fragment>
  ));

  /**
   * Handle click - either call custom handler or follow link
   */
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (onClick) {
      e.preventDefault();
      onClick(item, index);
    }
    // If no onClick handler, default link behavior (href) will work
  };

  return (
    <div className="flowing-menu__item" ref={itemRef}>
      <a
        href={item.link}
        className="flowing-menu__item-link"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
      >
        {item.text}
      </a>
      <div className="flowing-menu__marquee" ref={marqueeRef}>
        <div className="flowing-menu__marquee-inner" ref={marqueeInnerRef}>
          {marqueeContent}
        </div>
      </div>
    </div>
  );
};

/**
 * Main FlowingMenu component
 */
const FlowingMenu: React.FC<FlowingMenuProps> = ({ items, onItemClick }) => {
  return (
    <div className="flowing-menu__wrap">
      <nav className="flowing-menu">
        {items.map((item, index) => (
          <MenuItem
            key={index}
            item={item}
            index={index}
            onClick={onItemClick}
          />
        ))}
      </nav>
    </div>
  );
};

export default FlowingMenu;
